import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { Box, getCSSVariable, Link, Row, useMedia } from '@opendoor/bricks-next';
import { getInitials } from 'components/landing-pages-v2/utils/getInitials';
import usePrevious from 'components/landing-pages-v2/utils/usePrevious';
import { useRouter } from 'next/router';

import { useObservability } from '../../../../../helpers/observability';
import MenuPanel from '../../menu/MenuPanel';
import { OpendoorLogo, OpendoorLogoBadge } from '../../OpendoorLogo';
import type { MegaNavItem, NavigationProps } from '../../types';
import { AccountSection } from '../AccountSection';
import { SkipLink } from '../SkipLink';
import { GLASS_HEADER_HEIGHT, redirectToLogin } from './constants';
import { GlassAccountSection } from './GlassAccountSection';
import { GlassMegaNav } from './GlassMegaNav';
import { GlassNavSearch } from './GlassNavSearch';

import styles from './GlassHeader.module.css';

interface GlassHeaderProps extends NavigationProps {
  megaNavData?: MegaNavItem[];
}

const GlassHeader = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaTitle,
  ctaLink,
  megaNavData,
}: GlassHeaderProps) => {
  const { pathname } = useRouter();
  const { largerThanSM } = useMedia();
  const { user: human, authentication } = useAuth();
  const { trackEvent } = useObservability();
  const isLoggedIn = authentication.state === 'authenticated';
  const initials = getInitials(human?.fullName, human?.firstName);
  const showLogin = !(pathname.includes('/login') || pathname.includes('/account'));

  const [isScrolled, setIsScrolled] = useState(false);
  const [isAddressHidden, setIsAddressHidden] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);
  const prevMobileMenuOpen = usePrevious(isMobileMenuOpen);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const addressObserverRef = useRef<IntersectionObserver | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let scrollHandler: (() => void) | null = null;
    let mutationObserver: MutationObserver | null = null;

    const rootMargin = `-${GLASS_HEADER_HEIGHT}px 0px 0px 0px`;

    const observeHero = (el: Element) => {
      observerRef.current?.disconnect();
      observerRef.current = new IntersectionObserver(
        ([entry]) => setIsScrolled(!entry.isIntersecting),
        { threshold: 0, rootMargin },
      );
      observerRef.current.observe(el);
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
      }
    };

    const observeAddress = (el: Element) => {
      addressObserverRef.current?.disconnect();
      addressObserverRef.current = new IntersectionObserver(
        ([entry]) => setIsAddressHidden(!entry.isIntersecting),
        { threshold: 0, rootMargin },
      );
      addressObserverRef.current.observe(el);
    };

    const heroSelector = '[data-hero]';
    const addressSelector =
      '[class*="hp-reskin-v2-address-entry-wrapper"],.hp-reskin-v2-address-entry-wrapper';

    const tryObserve = () => {
      const heroEl = document.querySelector(heroSelector);
      if (heroEl) observeHero(heroEl);

      const addressEl = document.querySelector(addressSelector);
      if (addressEl) observeAddress(addressEl);

      return { heroFound: !!heroEl, addressFound: !!addressEl };
    };

    const { heroFound, addressFound } = tryObserve();

    if (!heroFound) {
      scrollHandler = () => setIsScrolled(window.scrollY > GLASS_HEADER_HEIGHT);
      window.addEventListener('scroll', scrollHandler, { passive: true });
    }

    if (!heroFound || !addressFound) {
      mutationObserver = new MutationObserver(() => {
        const result = tryObserve();
        if (result.heroFound && result.addressFound) {
          mutationObserver?.disconnect();
        }
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observerRef.current?.disconnect();
      addressObserverRef.current?.disconnect();
      mutationObserver?.disconnect();
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
    };
  }, []);

  const handleMenuOpenChange = useCallback((itemName: string | null) => {
    setIsMenuOpen(itemName !== null);
  }, []);

  const handleMobileSearchExpandChange = useCallback((expanded: boolean) => {
    setIsMobileSearchExpanded(expanded);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const trackMenuClick = useCallback(() => {
    trackEvent('cta-click', 'menu', undefined, { categoryOverride: 'homepage' });
  }, [trackEvent]);

  const trackAccountClick = useCallback(() => {
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
  }, [trackEvent]);

  const exitComplete = () => {
    if (prevMobileMenuOpen === true) {
      const activeEl = document.activeElement;
      const isInputFocused =
        activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement;
      if (!isInputFocused) {
        hamburgerRef.current?.focus();
      }
    }
  };

  useEffect(() => {
    if (largerThanSM && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [largerThanSM, isMobileMenuOpen]);

  const anyMenuOpen = isMenuOpen || isMobileMenuOpen;

  let bgValue = 'transparent';
  if (anyMenuOpen) {
    bgValue = 'rgba(0, 0, 0, 1)';
  } else if (isScrolled) {
    bgValue = 'rgba(0, 0, 0, 0.85)';
  }

  const blurValue = isScrolled && !anyMenuOpen ? 'blur(12px)' : 'blur(3.5px)';

  const mobileWhiteHeader = isScrolled;
  const mobileFg = mobileWhiteHeader
    ? getCSSVariable('$contentPrimary', 'color')
    : getCSSVariable('$contentInversePrimary', 'color');
  const mobileBg = mobileWhiteHeader ? '#ffffff' : 'transparent';

  return (
    <>
      {!largerThanSM && (
        <MenuPanel
          slug={slug}
          menuPanelNavLinks={menuPanelNavLinks}
          mainNavLinks={mainNavLinks}
          ctaTitle={ctaTitle}
          ctaLink={ctaLink}
          isVisible={isMobileMenuOpen}
          setIsVisible={setIsMobileMenuOpen}
          exitComplete={exitComplete}
          megaNavData={megaNavData}
          variant="glass"
        />
      )}

      <Box
        display="block"
        zIndex={2000}
        height={GLASS_HEADER_HEIGHT}
        top={0}
        left={0}
        right={0}
        width="100%"
        $platform-web={{ position: 'fixed' }}
        borderRadius={0}
        overflow="visible"
        $smallerThanMD={{
          overflow: 'visible',
        }}
        className={styles.headerBar}
        style={
          {
            border: 'none',
            '--glass-header-bg': bgValue,
            '--glass-header-blur': blurValue,
            '--glass-header-mobile-bg': mobileBg,
            '--glass-header-mobile-border': mobileWhiteHeader
              ? 'rgba(0, 0, 0, 0.06)'
              : 'rgba(255, 255, 255, 0.08)',
          } as React.CSSProperties
        }
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={GLASS_HEADER_HEIGHT}
        >
          <Box
            width="100%"
            height={GLASS_HEADER_HEIGHT}
            maxWidth="$328"
            px="$5"
            alignItems="center"
            $platform-web={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto' }}
          >
            <SkipLink />

            <Row alignItems="center" pr="$2" zIndex={5} gridColumn="1 / 2" gridRow="1 / 2">
              <Link
                href="/"
                analyticsName="cosmos-glass-nav-logo"
                aria-label="Go to Home page"
                display="flex"
                alignItems="center"
                color="$contentInversePrimary"
                $smallerThanMD={{ display: 'none' }}
              >
                <OpendoorLogo width="96px" />
              </Link>
              <Link
                href="/"
                analyticsName="cosmos-glass-nav-logo"
                aria-label="Go to Home page"
                display={isMobileSearchExpanded ? 'none' : 'flex'}
                alignItems="center"
                color={mobileFg}
                $largerThanSM={{ display: 'none' }}
              >
                <OpendoorLogoBadge />
              </Link>
              <Box $smallerThanMD={{ display: 'none' }}>
                <GlassNavSearch isVisible={isAddressHidden} />
              </Box>
            </Row>

            <GlassMegaNav
              slug={slug}
              megaNavData={megaNavData}
              onOpenChange={handleMenuOpenChange}
            />

            <Box
              $largerThanSM={{ display: 'none' }}
              display="flex"
              alignItems="center"
              flex={1}
              minWidth={0}
              px="$1"
              $platform-web={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}
            >
              <GlassNavSearch
                isVisible={isAddressHidden && !isMobileMenuOpen}
                onExpandChange={handleMobileSearchExpandChange}
              />
            </Box>

            <Box
              display="flex"
              alignItems="center"
              gap="$4"
              flexShrink={0}
              $platform-web={{ gridRow: '1 / 2', gridColumn: '-1 / -2' }}
            >
              <GlassAccountSection
                isLoggedIn={isLoggedIn}
                showLogin={showLogin}
                initials={initials}
              />

              <Box
                $largerThanSM={{ display: 'none' }}
                display={isMobileSearchExpanded ? 'none' : 'flex'}
                ml="$2"
              >
                <AccountSection
                  isLoggedIn={isLoggedIn}
                  showLogin={showLogin}
                  initials={initials}
                  redirectToLogin={redirectToLogin}
                  trackAccountClick={trackAccountClick}
                  trackMenuClick={trackMenuClick}
                  onMobileMenuToggle={toggleMobileMenu}
                  isMobileMenuVisible={isMobileMenuOpen}
                  hamburgerRef={hamburgerRef}
                  foregroundColor={mobileFg}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default GlassHeader;
